import { useState, useEffect, useRef, useCallback } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";
import { ADJ, ADJ_DEFAULTS, ADJ_LABELS, CURVE_PRESETS } from "../engine/constants.js";

function buildCurvesLUT(points) {
  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  const lut = new Uint8Array(256);
  for (let x = 0; x < 256; x++) {
    let lo = sorted[0], hi = sorted[sorted.length - 1];
    for (let i = 0; i < sorted.length - 1; i++) {
      if (x >= sorted[i][0] && x <= sorted[i+1][0]) { lo = sorted[i]; hi = sorted[i+1]; break; }
    }
    const t = hi[0] === lo[0] ? 0 : (x - lo[0]) / (hi[0] - lo[0]);
    lut[x] = Math.max(0, Math.min(255, Math.round(lo[1] + t * (hi[1] - lo[1]))));
  }
  return lut;
}

/**
 * AdjustmentDialog — add or edit an adjustment layer.
 *
 * Props:
 *   open          boolean
 *   editIdx       number|null    — null = adding new layer
 *   editType      string         — ADJ.* constant
 *   layers        array          — layer list for Reset fallback
 *   engRef        React ref      — engine ref (null in worker mode)
 *   onCommit      (type, params, idx) => void
 *   onClose       () => void
 *   forceUpdate   () => void
 */
export function AdjustmentDialog({ open, editIdx, editType: initialType, layers, engRef, onCommit, onClose, forceUpdate }) {
  const [type,   setType]   = useState(initialType ?? ADJ.BRIGHTNESS);
  const [params, setParams] = useState({});
  const adjOrigRef = useRef(null);

  // Sync type/params when dialog opens
  useEffect(() => {
    if (!open) {
      adjOrigRef.current = null;
      // Clear any stale preview on the layer
      const eng = engRef?.current;
      if (editIdx != null && eng) {
        const L = eng.s.layers[editIdx];
        if (L?._adjPreviewParams) {
          L._adjPreviewParams = null;
          L._adjDirty = true;
          L._glDirty = true;
          L._adjCacheParams = null;
          eng.s.dirty = true;
        }
      }
      return;
    }
    if (editIdx != null) {
      // Editing existing — load current params
      const L = layers[editIdx];
      const p = L?.adjParams ?? {};
      setType(L?.adjType ?? initialType ?? ADJ.BRIGHTNESS);
      setParams({ ...p });
      adjOrigRef.current = JSON.parse(JSON.stringify(p));
    } else {
      // Adding new — use defaults for initial type
      const t = initialType ?? ADJ.BRIGHTNESS;
      setType(t);
      setParams({ ...(ADJ_DEFAULTS[t] ?? {}) });
      adjOrigRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When type changes while adding new, reset params to defaults
  const handleTypeChange = useCallback((t) => {
    setType(t);
    setParams({ ...(ADJ_DEFAULTS[t] ?? {}) });
  }, []);

  // Live preview — single-thread only (engRef.current is null in worker mode)
  useEffect(() => {
    if (!open || editIdx == null) return;
    const eng = engRef?.current;
    if (!eng) return;
    const L = eng.s.layers[editIdx];
    if (!L || L.type !== "adjustment") return;
    // Save original once
    if (!adjOrigRef.current) {
      adjOrigRef.current = JSON.parse(JSON.stringify(L.adjParams ?? {}));
    }
    L._adjPreviewParams = { ...L.adjParams, ...params };
    L._adjDirty = true;
    L._glDirty = true;
    L._adjCacheParams = null;
    eng.s.dirty = true;
    forceUpdate?.();
  }, [open, editIdx, params, engRef, forceUpdate]);

  const handleCommit = useCallback(() => {
    onCommit(type, params, editIdx);
    onClose();
  }, [type, params, editIdx, onCommit, onClose]);

  const handleCancel = useCallback(() => {
    const eng = engRef?.current;
    if (editIdx != null && eng) {
      const L = eng.s.layers[editIdx];
      if (L) {
        L._adjPreviewParams = null;
        L._adjDirty = true;
        L._glDirty = true;
        L._adjCacheParams = null;
        eng.s.dirty = true;
      }
    }
    onClose();
    forceUpdate?.();
  }, [editIdx, engRef, onClose, forceUpdate]);

  const handleReset = useCallback(() => {
    if (editIdx == null) {
      setParams({ ...(ADJ_DEFAULTS[type] ?? {}) });
    } else {
      const L = layers[editIdx];
      if (L) setParams({ ...(L.adjParams ?? {}) });
    }
  }, [editIdx, type, layers]);

  if (!open) return null;

  return (
    <Modal onClose={handleCancel}>
      <div style={{ padding:"4px 0" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:12 }}>
          {editIdx != null ? "Edit Adjustment Layer" : "New Adjustment Layer"}
        </div>

        {/* Type selector — only shown when adding new */}
        {editIdx == null && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:V.muted, marginBottom:6 }}>Type</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
              {Object.entries(ADJ_LABELS).map(([k, v]) => (
                <div key={k} onClick={() => handleTypeChange(k)}
                  style={{ padding:"5px 10px", fontSize:10, borderRadius:4, cursor:"pointer", fontFamily:V.mono,
                    background:   type===k ? "rgba(167,139,250,0.15)" : V.panelActive,
                    border:      `1px solid ${type===k ? "#a78bfa" : V.panelBorder2}`,
                    color:        type===k ? "#a78bfa" : V.muted,
                    transition:   "all 0.12s" }}>
                  {v}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Type badge when editing */}
        {editIdx != null && (
          <div style={{ fontSize:10, color:"#a78bfa", marginBottom:10, padding:"4px 8px", borderRadius:4,
            background:"rgba(167,139,250,0.06)", border:"1px solid rgba(167,139,250,0.15)" }}>
            {ADJ_LABELS[type] ?? type}
          </div>
        )}

        {/* Parameter controls */}
        <AdjParams type={type} params={params} setParams={setParams} />

        <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginTop:8 }}>
          <OptBtn onClick={handleReset}>Reset</OptBtn>
          <OptBtn onClick={handleCancel}>Cancel</OptBtn>
          <OptBtn accent onClick={handleCommit}>
            {editIdx != null ? "Update" : "Add Layer"}
          </OptBtn>
        </div>
      </div>
    </Modal>
  );
}

// ── Parameter controls by adjustment type ──

function AdjParams({ type, params, setParams }) {
  const set = useCallback((key, val) => setParams(p => ({ ...p, [key]: val })), [setParams]);

  if (type === ADJ.BRIGHTNESS) return (
    <>
      <AdjRow label="Brightness" value={params.brightness??0} min={-100} max={100} onChange={v => set("brightness", v)}/>
      <AdjRow label="Contrast"   value={params.contrast??0}   min={-100} max={100} onChange={v => set("contrast",   v)}/>
    </>
  );

  if (type === ADJ.LEVELS) return (
    <>
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Input Levels</div>
        <div style={{ display:"flex", gap:8 }}>
          <div style={{ flex:1 }}><AdjRow label="Black" value={params.inBlack??0}   min={0} max={254} onChange={v => set("inBlack", v)}/></div>
          <div style={{ flex:1 }}><AdjRow label="White" value={params.inWhite??255} min={1} max={255} onChange={v => set("inWhite", v)}/></div>
        </div>
        <AdjRow label="Gamma" value={Math.round((params.gamma??1)*100)} min={10} max={300}
          onChange={v => set("gamma", v/100)} fmt={v => (v/100).toFixed(2)}/>
      </div>
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Output Levels</div>
        <div style={{ display:"flex", gap:8 }}>
          <div style={{ flex:1 }}><AdjRow label="Black" value={params.outBlack??0}   min={0} max={254} onChange={v => set("outBlack", v)}/></div>
          <div style={{ flex:1 }}><AdjRow label="White" value={params.outWhite??255} min={1} max={255} onChange={v => set("outWhite", v)}/></div>
        </div>
      </div>
    </>
  );

  if (type === ADJ.HUESAT) return (
    <>
      <AdjRow label="Hue"        value={params.hue??0}        min={-180} max={180} onChange={v => set("hue",        v)} fmt={v => v+"°"}/>
      <AdjRow label="Saturation" value={params.saturation??0} min={-100} max={100} onChange={v => set("saturation", v)}/>
      <AdjRow label="Lightness"  value={params.lightness??0}  min={-100} max={100} onChange={v => set("lightness",  v)}/>
    </>
  );

  if (type === ADJ.INVERT) return (
    <div style={{ fontSize:10, color:V.muted, padding:"8px 0" }}>
      Inverts all colors below this layer. No parameters needed — toggle visibility to compare.
    </div>
  );

  if (type === ADJ.COLORBAL) return (
    <>
      {[
        { label:"Shadows",    prefix:"shadow", color:"#60a5fa" },
        { label:"Midtones",   prefix:"mid",    color:"#4ade80" },
        { label:"Highlights", prefix:"high",   color:"#fbbf24" },
      ].map(zone => (
        <div key={zone.prefix} style={{ marginBottom:8 }}>
          <div style={{ fontSize:10, fontWeight:600, color:zone.color, marginBottom:3, letterSpacing:"0.5px" }}>
            {zone.label}
          </div>
          <AdjRow label="Cyan ↔ Red"   value={params[zone.prefix+"R"]??0} min={-100} max={100} onChange={v => set(zone.prefix+"R", v)}/>
          <AdjRow label="Mag ↔ Grn"    value={params[zone.prefix+"G"]??0} min={-100} max={100} onChange={v => set(zone.prefix+"G", v)}/>
          <AdjRow label="Yel ↔ Blu"    value={params[zone.prefix+"B"]??0} min={-100} max={100} onChange={v => set(zone.prefix+"B", v)}/>
        </div>
      ))}
      <label style={{ fontSize:10, color:V.muted, cursor:"pointer", display:"flex", alignItems:"center", gap:4, marginBottom:6 }}>
        <input type="checkbox" checked={params.preserveLum !== false}
          onChange={e => set("preserveLum", e.target.checked)}
          style={{ width:12, height:12, margin:0 }}/>
        Preserve Luminosity
      </label>
    </>
  );

  if (type === ADJ.CURVES) return (
    <CurvesEditor params={params} setParams={setParams} />
  );

  // ════════════════ Phase 6: Color Grading Panels ════════════════

  if (type === ADJ.VIBRANCE) return (
    <AdjRow label="Vibrance" value={params.amount??0} min={-100} max={100} onChange={v => set("amount", v)}/>
  );

  if (type === ADJ.CHANNEL_MIX) {
    const channels = [
      { label: "Output Red",   keys: ["rr","rg","rb"], color: "#f87171" },
      { label: "Output Green", keys: ["gr","gg","gb"], color: "#4ade80" },
      { label: "Output Blue",  keys: ["br","bg","bb"], color: "#60a5fa" },
    ];
    const srcLabels = ["Red","Green","Blue"];
    return (<>
      {channels.map(ch => (
        <div key={ch.label} style={{ marginBottom:8 }}>
          <div style={{ fontSize:10, color:ch.color, marginBottom:4 }}>{ch.label}</div>
          {ch.keys.map((k,i) => (
            <AdjRow key={k} label={srcLabels[i]} value={params[k]??0} min={-200} max={200}
              onChange={v => set(k, v)} fmt={v => v+"%"}/>
          ))}
        </div>
      ))}
    </>);
  }

  if (type === ADJ.BW_MIX) return (<>
    <div style={{ fontSize:10, color:V.muted, marginBottom:6 }}>Channel contribution to grayscale</div>
    <AdjRow label="Red"   value={params.red??40}   min={-200} max={200} onChange={v => set("red",   v)} fmt={v => v+"%"}/>
    <AdjRow label="Green" value={params.green??34}  min={-200} max={200} onChange={v => set("green", v)} fmt={v => v+"%"}/>
    <AdjRow label="Blue"  value={params.blue??26}   min={-200} max={200} onChange={v => set("blue",  v)} fmt={v => v+"%"}/>
  </>);

  if (type === ADJ.SELECTIVE) {
    const ranges = ["reds","yellows","greens","cyans","blues","magentas","neutrals","whites","blacks"];
    return (<>
      <div style={{ fontSize:10, color:V.muted, marginBottom:6 }}>Target hue range</div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginBottom:8 }}>
        {ranges.map(r => (
          <div key={r} onClick={() => set("range", r)}
            style={{ padding:"3px 7px", fontSize:9, borderRadius:3, cursor:"pointer", fontFamily:V.mono,
              background: (params.range||"reds")===r ? "rgba(167,139,250,0.15)" : V.panelActive,
              border: `1px solid ${(params.range||"reds")===r ? "#a78bfa" : V.panelBorder2}`,
              color: (params.range||"reds")===r ? "#a78bfa" : V.muted, textTransform:"capitalize" }}>
            {r}
          </div>
        ))}
      </div>
      <AdjRow label="Cyan"    value={params.cyan??0}    min={-100} max={100} onChange={v => set("cyan",    v)}/>
      <AdjRow label="Magenta" value={params.magenta??0} min={-100} max={100} onChange={v => set("magenta", v)}/>
      <AdjRow label="Yellow"  value={params.yellow??0}  min={-100} max={100} onChange={v => set("yellow",  v)}/>
      <AdjRow label="Black"   value={params.black??0}   min={-100} max={100} onChange={v => set("black",   v)}/>
    </>);
  }

  if (type === ADJ.GRAD_MAP) {
    const stops = params.stops || [{pos:0,color:"#000000"},{pos:1,color:"#ffffff"}];
    const updateStop = (idx, color) => {
      const ns = stops.map((s,i) => i===idx ? {...s, color} : s);
      setParams(p => ({...p, stops: ns}));
    };
    const addStop = () => {
      const ns = [...stops, {pos:0.5, color:"#808080"}].sort((a,b) => a.pos - b.pos);
      setParams(p => ({...p, stops: ns}));
    };
    const removeStop = (idx) => {
      if (stops.length <= 2) return;
      setParams(p => ({...p, stops: stops.filter((_,i) => i !== idx)}));
    };
    return (<>
      <div style={{ fontSize:10, color:V.muted, marginBottom:6 }}>Map luminance to gradient colors</div>
      <div style={{ display:"flex", height:24, borderRadius:4, overflow:"hidden", marginBottom:8,
        background:`linear-gradient(to right, ${stops.map(s => s.color+" "+Math.round(s.pos*100)+"%").join(", ")})` }}/>
      {stops.map((s,i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
          <span style={{ fontSize:9, color:V.ghost, width:30 }}>{Math.round(s.pos*100)}%</span>
          <input type="color" value={s.color} onChange={e => updateStop(i, e.target.value)}
            style={{ width:28, height:20, border:"none", cursor:"pointer", background:"transparent" }}/>
          <span style={{ fontSize:9, color:V.muted }}>{s.color}</span>
          {stops.length > 2 && <span onClick={() => removeStop(i)} style={{ fontSize:9, color:V.ghost, cursor:"pointer", marginLeft:"auto" }}>✕</span>}
        </div>
      ))}
      <div onClick={addStop} style={{ fontSize:9, color:V.accentL, cursor:"pointer", marginTop:4 }}>+ Add stop</div>
    </>);
  }

  if (type === ADJ.SHADOW_HL) return (<>
    <AdjRow label="Shadows"     value={params.shadows??0}    min={-100} max={100} onChange={v => set("shadows",    v)}/>
    <AdjRow label="Highlights"  value={params.highlights??0} min={-100} max={100} onChange={v => set("highlights", v)}/>
    <AdjRow label="Tonal Width" value={params.tonalWidth??50} min={10}  max={100} onChange={v => set("tonalWidth", v)} fmt={v => v+"%"}/>
  </>);

  return null;
}

function CurvesEditor({ params, setParams }) {
  const channel = params.channel ?? "master";
  const points  = params.points  ?? [[0,0],[255,255]];

  const setChannel = c => setParams(p => ({ ...p, channel: c }));
  const setPoints  = pts => setParams(p => ({ ...p, points: pts }));

  const CHANNELS = [
    { k:"master", l:"Master", c:V.muted   },
    { k:"r",      l:"R",      c:"#f87171" },
    { k:"g",      l:"G",      c:"#4ade80" },
    { k:"b",      l:"B",      c:"#60a5fa" },
  ];
  const chCol = { master:V.muted, r:"#f87171", g:"#4ade80", b:"#60a5fa" }[channel] ?? V.muted;

  return (
    <>
      <div style={{ display:"flex", gap:4, marginBottom:8 }}>
        {CHANNELS.map(ch => (
          <span key={ch.k} onClick={() => setChannel(ch.k)}
            style={{ fontSize:9, padding:"2px 7px", borderRadius:3, cursor:"pointer", fontFamily:V.mono,
              color: ch.c,
              border: channel===ch.k ? `1px solid ${ch.c}` : "1px solid transparent",
              background: channel===ch.k ? `${ch.c}22` : "transparent" }}>
            {ch.l}
          </span>
        ))}
      </div>

      <div style={{ position:"relative", marginBottom:8 }}>
        <canvas width={256} height={256}
          style={{ width:256, height:256, borderRadius:4, border:`1px solid ${V.panelBorder2}`, cursor:"crosshair", display:"block" }}
          ref={el => {
            if (!el) return;
            const cx = el.getContext("2d", { willReadFrequently:true });
            const sorted = [...points].sort((a,b) => a[0]-b[0]);
            const lut = buildCurvesLUT(sorted);
            cx.fillStyle = "#111"; cx.fillRect(0,0,256,256);
            cx.strokeStyle = "#222"; cx.lineWidth = 1;
            for (let i = 64; i < 256; i += 64) {
              cx.beginPath(); cx.moveTo(i,0);   cx.lineTo(i,256);   cx.stroke();
              cx.beginPath(); cx.moveTo(0,i);   cx.lineTo(256,i);   cx.stroke();
            }
            cx.strokeStyle = "#333"; cx.lineWidth = 1; cx.setLineDash([3,3]);
            cx.beginPath(); cx.moveTo(0,255); cx.lineTo(255,0); cx.stroke();
            cx.setLineDash([]);
            cx.strokeStyle = chCol; cx.lineWidth = 2; cx.beginPath();
            for (let i = 0; i < 256; i++) {
              const y = 255 - lut[i];
              if (i === 0) cx.moveTo(i,y); else cx.lineTo(i,y);
            }
            cx.stroke();
            sorted.forEach(pt => {
              cx.beginPath(); cx.arc(pt[0], 255-pt[1], 5, 0, Math.PI*2);
              cx.fillStyle = chCol; cx.fill();
              cx.strokeStyle = "#fff"; cx.lineWidth = 1.5; cx.stroke();
            });
          }}
          onMouseDown={e => {
            e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            const mx = Math.round(e.clientX - r.left);
            const my = Math.round(255 - (e.clientY - r.top));
            const sorted = [...points].sort((a,b) => a[0]-b[0]);
            let hitIdx = -1;
            for (let i = 0; i < sorted.length; i++) {
              if (Math.abs(sorted[i][0]-mx) < 10 && Math.abs(sorted[i][1]-my) < 10) { hitIdx = i; break; }
            }
            if (hitIdx >= 0) {
              const isEnd = hitIdx === 0 || hitIdx === sorted.length-1;
              const onM = e2 => {
                const nx = Math.max(0, Math.min(255, Math.round(e2.clientX - r.left)));
                const ny = Math.max(0, Math.min(255, Math.round(255 - (e2.clientY - r.top))));
                const np = [...points].sort((a,b) => a[0]-b[0]);
                if (isEnd) np[hitIdx] = [np[hitIdx][0], ny];
                else np[hitIdx] = [nx, ny];
                setPoints(np);
              };
              const onU = () => { document.removeEventListener("mousemove", onM); document.removeEventListener("mouseup", onU); };
              document.addEventListener("mousemove", onM);
              document.addEventListener("mouseup", onU);
            } else {
              setPoints([...points, [mx, my]]);
            }
          }}
          onContextMenu={e => {
            e.preventDefault(); e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            const mx = Math.round(e.clientX - r.left);
            const my = Math.round(255 - (e.clientY - r.top));
            const sorted = [...points].sort((a,b) => a[0]-b[0]);
            for (let i = 1; i < sorted.length-1; i++) {
              if (Math.abs(sorted[i][0]-mx) < 10 && Math.abs(sorted[i][1]-my) < 10) {
                sorted.splice(i, 1);
                setPoints(sorted);
                break;
              }
            }
          }}
        />
        <div style={{ fontSize:8, color:V.ghost, display:"flex", justifyContent:"space-between", marginTop:2 }}>
          <span>0 (shadows)</span><span>128 (midtones)</span><span>255 (highlights)</span>
        </div>
      </div>

      <div style={{ fontSize:9, color:V.ghost, marginBottom:6 }}>
        Click to add points · Drag to adjust · Right-click to delete
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginBottom:8 }}>
        {Object.entries(CURVE_PRESETS).map(([k, v]) => (
          <div key={k} onClick={() => setPoints([...v.points])}
            style={{ padding:"3px 7px", fontSize:9, borderRadius:3, cursor:"pointer", fontFamily:V.mono,
              background:V.panelActive, border:`1px solid ${V.panelBorder2}`, color:V.muted, transition:"all 0.1s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor="#555"; e.currentTarget.style.color=V.text; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=V.panelBorder2; e.currentTarget.style.color=V.muted; }}>
            {v.label}
          </div>
        ))}
      </div>
    </>
  );
}
